import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { userAPI } from "../../api/user/user";

const initialState = {
    user: null,
    statuses: {
        uploading: null,
        updating: null,
        logout: null
    },
    errors: {
        uploading: null,
        updating: null,
        logout: null
    },
    isSaving: false,
    isEditingMode: false,
    unauthorized: false,
};

export const getUser = createAsyncThunk(
    'user/getUser',
    async (_, { rejectWithValue }) => {
        const resp = await userAPI.getUser();
        if(resp.ok) {
            return {user: resp.user};
        }
        if(resp.unauthorized) {
            return rejectWithValue({err: "unauthorized"});
        }
        if(resp.err) {
            return rejectWithValue({err: resp.err});
        }

        return rejectWithValue({err: "something_went_wrong"});
    }
)

export const updateUser = createAsyncThunk(
    'user/updateUser',
    async ({data}, { rejectWithValue }) => {
        const resp = await userAPI.updateUser(data);
        if(resp.ok) {
            return {user: resp.user};
        }
        if(resp.unauthorized) {
            return rejectWithValue({err: "unauthorized"});
        }
        if(resp.err) {
            return rejectWithValue({err: resp.err});
        }
    }
)

export const logout = createAsyncThunk(
    'user/logout',
    async (_, { rejectWithValue }) => {
        let resp = await userAPI.logout();
        if(resp.ok) {
            return {ok: true};
        }
        if(resp.err) {
            return rejectWithValue({err: resp.err});
        }
        return rejectWithValue({err: "something_went_wrong"});
    }
)

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        setUserUpdatingStatus(state, action) {
            state.statuses.updating = action.payload;
        },
        setUserGettingStatus(state, action) {
            state.statuses.uploading = action.payload;
        },
        setUnauthorized(state, action) {
            state.unauthorized = action.payload;
        },
        setIsEditingMode(state, action) {
            state.isEditingMode = action.payload;
        },
        setIsSaving(state, action) {
            state.isSaving = action.payload;
        },
        setLogoutStatus(state, action) {
            state.statuses.logout = action.payload;
        }
    },
    extraReducers(builder) {
        builder
            .addCase(getUser.pending, (state, action) => {
                state.statuses.uploading = 'pending';
            })
            .addCase(getUser.fulfilled, (state, action) => {
                state.statuses.uploading = 'fulfilled';
                state.user = action.payload?.user ?? null;
            })
            .addCase(getUser.rejected, (state, action) => {
                state.statuses.uploading = 'rejected';
                state.errors.uploading = action.payload?.err ?? "something_went_wrong";
                if(action.payload?.err === "unauthorized") {
                    state.unauthorized = true;
                }
            })
            .addCase(updateUser.pending, (state, action) => {
                state.statuses.updating = 'pending';
            })
            .addCase(updateUser.fulfilled, (state, action) => {
                state.statuses.updating = 'fulfilled';
                state.user = action.payload?.user ?? null;
            })
            .addCase(updateUser.rejected, (state, action) => {
                state.statuses.updating = 'rejected';
                state.errors.updating = action.payload?.err ?? "something_went_wrong";
                if(action.payload?.err === "unauthorized") {
                    state.unauthorized = true;
                }
            })
            .addCase(logout.pending, (state, action) => {
                state.statuses.logout = 'pending';
                state.statuses.logout = null;
            })
            .addCase(logout.fulfilled, (state, action) => {
                state.statuses.logout = 'fulfilled';
                state.errors.logout = null;
                state.user = null;
            })
            .addCase(logout.rejected, (state, action) => {
                state.statuses.logout = 'rejected';
                state.errors.logout = action.payload?.err ?? "something_went_wrong";
            })
    }
})

export default userSlice.reducer;

export const { setUserUpdatingStatus, setIsEditingMode, setIsSaving, setUnauthorized, setLogoutStatus, setUserGettingStatus } = userSlice.actions;

export const selectUser = state => state.user.user;
export const selectIsEditingMode = state => state.user.isEditingMode;
export const selectUserUpdatingStatus = state => state.user.statuses.updating;
export const selectUserUpdatingError = state => state.user.errors.updating;
export const selectIsSaving = state => state.user.isSaving;